import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ProjectService from "../services/ProjectService";
import ClientService from "../services/ClientService";
import BillingItemsService from "../services/BillingItemsService";
import { AuthContext } from "../context/AuthContext";
import { FaEdit, FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { ChevronLeft } from "lucide-react";

const ViewProject = () => {
  const { projectId, activeTab: urlActiveTab } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [billingItems, setBillingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEmp, setIsEmp] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [activeTab, setActiveTab] = useState(urlActiveTab || "details");
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [newBillingItem, setNewBillingItem] = useState({
    title: "",
    description: "",
    amount: "",
    currency: "INR",
    dueDate: "",
    status: "PENDING",
  });

  // Edit project state
  const [editProject, setEditProject] = useState({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "ACTIVE",
  });
  const [isEditingProject, setIsEditingProject] = useState(false);

  // Update URL when active tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/dashboard/projects/${projectId}/${tab}`, { replace: true });
  };

  // Make sure activeTab stays in sync with URL parameter
  useEffect(() => {
    if (urlActiveTab && ["details", "updates", "billing", "edit"].includes(urlActiveTab)) {
      setActiveTab(urlActiveTab);
    } else if (!urlActiveTab) {
      // If no tab specified in URL, use default and update URL
      setActiveTab("details");
      navigate(`/dashboard/projects/${projectId}/details`, { replace: true });
    }
  }, [urlActiveTab, projectId, navigate]);

  useEffect(() => {
    // Check user permissions
    const adminCheck = ["ADMIN", "CEO", "VAIBHAV"].includes(user?.role);
    setIsAdmin(adminCheck);
    setIsClient(user?.role === "CLIENT");
    setIsEmp(user?.role === "EMP");

    const fetchProjectData = async () => {
      try {
        setLoading(true);
        const projectData = await ProjectService.getProjectById(projectId);
        setProject(projectData);

        // Set edit project data
        setEditProject({
          name: projectData.name || "",
          description: projectData.description || "",
          startDate: projectData.startDate ? new Date(projectData.startDate).toISOString().split('T')[0] : "",
          endDate: projectData.endDate ? new Date(projectData.endDate).toISOString().split('T')[0] : "",
          status: projectData.status || "ACTIVE",
        });

        // Fetch client details if available
        if (projectData.client && typeof projectData.client === "object") {
          setClient(projectData.client);
        } else if (projectData.client) {
          // If client is just an ID, fetch the client data
          const clientData = await ClientService.getClientById(
            projectData.client
          );
          setClient(clientData);
        }

        // Fetch billing items for the project
        const billingData = await BillingItemsService.getBillingItemsByProject(
          projectId
        );
        setBillingItems(billingData);
        console.log(billingData);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching project data:", err);
        setError("Failed to load project details.");
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId, user]);

  const handleDeleteProject = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this project? This action cannot be undone."
      )
    ) {
      try {
        setLoading(true);
        await ProjectService.deleteProject(projectId);
        navigate("/dashboard/projects");
      } catch (err) {
        console.error("Error deleting project:", err);
        setError("Failed to delete project.");
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleBillingInputChange = (e) => {
    const { name, value } = e.target;
    setNewBillingItem({
      ...newBillingItem,
      [name]: value,
    });
  };

  const handleCreateBillingItem = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const billingData = {
        ...newBillingItem,
        projectId: projectId,
        clientId: client._id || client,
      };

      await BillingItemsService.createBillingItem(billingData);

      // Reset form and fetch updated billing items
      setNewBillingItem({
        title: "",
        description: "",
        amount: "",
        currency: "INR",
        dueDate: "",
        status: "PENDING",
      });
      setShowBillingForm(false);

      // Refresh billing items
      const updatedBillingItems =
        await BillingItemsService.getBillingItemsByProject(projectId);
      setBillingItems(updatedBillingItems);

      setLoading(false);
    } catch (err) {
      console.error("Error creating billing item:", err);
      setError("Failed to create billing item.");
      setLoading(false);
    }
  };

  const handleDeleteBillingItem = async (billingItemId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this billing item? This action cannot be undone."
      )
    ) {
      try {
        setLoading(true);
        await BillingItemsService.deleteBillingItem(billingItemId);

        // Refresh billing items
        const updatedBillingItems =
          await BillingItemsService.getBillingItemsByProject(projectId);
        setBillingItems(updatedBillingItems);

        setLoading(false);
      } catch (err) {
        console.error("Error deleting billing item:", err);
        setError("Failed to delete billing item.");
        setLoading(false);
      }
    }
  };

  // Handle edit project form input changes
  const handleEditProjectChange = (e) => {
    const { name, value } = e.target;
    setEditProject({
      ...editProject,
      [name]: value,
    });
  };

  // Handle edit project form submission
  const handleEditProjectSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsEditingProject(true);
      
      // Prepare project data for update
      const projectUpdateData = {
        name: editProject.name,
        description: editProject.description,
        startDate: editProject.startDate ? new Date(editProject.startDate) : null,
        endDate: editProject.endDate ? new Date(editProject.endDate) : null,
        status: editProject.status,
      };

      // Remove null values
      Object.keys(projectUpdateData).forEach(key => {
        if (projectUpdateData[key] === null || projectUpdateData[key] === "") {
          delete projectUpdateData[key];
        }
      });

      const updatedProject = await ProjectService.updateProject(projectId, projectUpdateData);
      setProject(updatedProject);
      
      // Switch back to details tab
      setActiveTab("details");
      navigate(`/dashboard/projects/${projectId}/details`, { replace: true });
      
      setIsEditingProject(false);
    } catch (err) {
      console.error("Error updating project:", err);
      setError("Failed to update project.");
      setIsEditingProject(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    let bgColor = "bg-gray-100 text-gray-800";

    switch (status) {
      case "ACTIVE":
        bgColor = "bg-green-100 text-green-800";
        break;
      case "COMPLETED":
        bgColor = "bg-blue-100 text-blue-800";
        break;
      case "PLANNING":
        bgColor = "bg-yellow-100 text-yellow-800";
        break;
      case "PAUSED":
        bgColor = "bg-orange-100 text-orange-800";
        break;
      case "CANCELLED":
        bgColor = "bg-red-100 text-red-800";
        break;
      case "DRAFT":
        bgColor = "bg-gray-100 text-gray-800";
        break;
      case "PENDING":
        bgColor = "bg-yellow-100 text-yellow-800";
        break;
      case "PAID":
        bgColor = "bg-green-100 text-green-800";
        break;
      case "OVERDUE":
        bgColor = "bg-red-100 text-red-800";
        break;
      default:
        bgColor = "bg-gray-100 text-gray-800";
    }

    return (
      <span
        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor}`}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md my-4" role="alert">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div
        className="bg-yellow-100 text-yellow-700 p-4 rounded-md my-4"
        role="alert"
      >
        <p className="font-bold">Project Not Found</p>
        <p>
          The project you're looking for doesn't exist or you don't have
          permission to view it.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6  mx-auto">
      <div className="flex justify-between items-start mb-6">
        <div className="items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/dashboard/project")}
              className="mr-4 text-gray-800 hover:text-gray-700"
            >
              <ChevronLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">
              {project.name}{" "}
              <span className="mt-2 mx-4">
                <StatusBadge status={project.status} />
              </span>
            </h1>
          </div>
        </div>

        {isAdmin && (
          <div className="flex space-x-2">
            <button
              onClick={() => handleTabChange("edit")}
              className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
            >
              Edit Project
            </button>
            <button
              onClick={handleDeleteProject}
              className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      {client && (
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange("details")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "details"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Project Details
            </button>
            <button
              onClick={() => handleTabChange("updates")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "updates"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Updates
            </button>
            {isAdmin && <button
              onClick={() => handleTabChange("billing")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "billing"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
              Billing
            </button>
}
            {isAdmin && (
              <button
                onClick={() => handleTabChange("edit")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "edit"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                Edit Project
              </button>
            )}
          </nav>
        </div>
      )}

      {/* Project Details Tab */}
      {activeTab === "details" && (
        <div className="space-y-6">
          {/* Project Info Section */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Project Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Project Name
                </p>
                <p className="text-gray-900 mt-1">{project.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <p className="text-gray-900 mt-1">
                  <StatusBadge status={project.status} />
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Start Date</p>
                <p className="text-gray-900 mt-1">
                  {formatDate(project.startDate)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">End Date</p>
                <p className="text-gray-900 mt-1">
                  {formatDate(project.endDate)}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm font-medium text-gray-500">Description</p>
              <p className="text-gray-900 mt-1">
                {project.description || "No description provided."}
              </p>
            </div>
          </div>

          {/* Client Info Section */}
          {client && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Client Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Company Name
                  </p>
                  <p className="text-gray-900 mt-1">
                    {client.companyName || "Not specified"}
                  </p>
                </div>
                {client.user && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Contact Person
                      </p>
                      <p className="text-gray-900 mt-1">
                        {client.user.name || "Not specified"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="text-gray-900 mt-1">
                        {client.user.email || "Not specified"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Phone</p>
                      <p className="text-gray-900 mt-1">
                        {client.user.phone || "Not specified"}
                      </p>
                    </div>
                  </>
                )}
              </div>
              {isAdmin && (
                <div className="mt-4">
                  <button
                    onClick={() =>
                      navigate(
                        `/dashboard/clients/user/${client.user?._id || client.user
                        }`
                      )
                    }
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View Client Profile
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="space-y-6">
            {isAdmin && !client && (
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() =>
                    navigate(`/dashboard/projects/${projectId}/add-update`)
                  }
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                >
                  Add Update
                </button>
              </div>
            )}
            {project.projectUpdate &&
              !client &&
              project.projectUpdate.length > 0 ? (
              <div className="space-y-4 bg-gray-50 p-4 rounded-md">
                <h2 className="text-lg font-semibold text-gray-700 mb-4">
                  All Update Of Project
                </h2>
                {project.projectUpdate.map((update) => (
                  <div
                    key={update._id}
                    className="bg-gray-50 p-4 rounded-md border-l-4 border-blue-500"
                  >
                    <div className="flex justify-between items-start">
                      <h3 className="text-lg font-medium text-gray-900">
                        {update.title}
                      </h3>
                      <div className="text-sm text-gray-500">
                        {new Date(update.createdAt).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-gray-700 whitespace-pre-line">
                      {update.content} 
                    </div>
                    {isAdmin && (
                      <div className="mt-4 flex space-x-4">
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/project-updates/${update._id}/edit`
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          <FaEdit className="text-lg" />
                        </button>
                        <button
                          onClick={async () => {
                            if (
                              window.confirm(
                                "Are you sure you want to delete this update?"
                              )
                            ) {
                              try {
                                // Assuming you have a service method for this
                                await ProjectService.deleteProjectUpdate(
                                  update._id
                                );
                                // Refresh project data
                                const updatedProject =
                                  await ProjectService.getProjectById(
                                    projectId
                                  );
                                setProject(updatedProject);
                              } catch (err) {
                                console.error("Error deleting update:", err);
                                setError("Failed to delete update.");
                              }
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              !client && (
                <div className="text-center py-8">
                  
                  <p className="text-gray-500">
                    No updates available for this project.
                  </p>
                 {isAdmin  && (
                    <button
                      onClick={() =>
                        navigate(`/dashboard/projects/${projectId}/add-update`)
                      }
                      className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Add the first update
                    </button>
                  )}
                </div>
              )
            )}

            
          </div>
        </div>
      )}

      {/* Edit Project Tab */}
      {activeTab === "edit" && isAdmin && (
        <div className="space-y-6">
          <div className="bg-gray-50 p-6 rounded-md">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Edit Project Details
            </h2>
            <form onSubmit={handleEditProjectSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={editProject.name}
                    onChange={handleEditProjectChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={editProject.startDate}
                    onChange={handleEditProjectChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={editProject.endDate}
                    onChange={handleEditProjectChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={editProject.status}
                    onChange={handleEditProjectChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="PLANNING">Planning</option>
                    <option value="ACTIVE">Active</option>
                    <option value="PAUSED">Paused</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={editProject.description}
                    onChange={handleEditProjectChange}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter project description..."
                  ></textarea>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => handleTabChange("details")}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isEditingProject}
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded disabled:opacity-50"
                >
                  {isEditingProject ? "Updating..." : "Update Project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Project Updates Tab */}
      {activeTab === "updates" && client && (
        <div className="space-y-6">
          {(isAdmin || isEmp) && (
            <div className="mb-4 flex justify-end">
              <button
                onClick={() =>
                  navigate(`/dashboard/projects/${projectId}/add-update`)
                }
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
              >
                Add Update
              </button>
            </div>
          )}

          {project.projectUpdate && project.projectUpdate.length > 0 ? (
            <div className="space-y-4">
              {project.projectUpdate.map((update) => (
                <div
                  key={update._id}
                  className="bg-gray-50 p-4 rounded-md border-l-4 border-blue-500"
                >
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-medium text-gray-900">
                      {update.title}
                    </h3>
                    <div className="text-sm text-gray-500">
                      {new Date(update.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                  </div>
                  <div className="mt-2 text-gray-700 whitespace-pre-line">
                    {update.content}
                  </div>
                  {(isAdmin ) && (
                    <div className="mt-4 flex gap-1">
                      <button
                        onClick={() =>
                          navigate(
                            `/dashboard/project-updates/${update._id}/edit`
                          )
                        }
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        <FaEdit className="text-lg" />
                      </button>
                      <button
                        onClick={async () => {
                          if (
                            window.confirm(
                              "Are you sure you want to delete this update?"
                            )
                          ) {
                            try {
                              // Assuming you have a service method for this
                              await ProjectService.deleteProjectUpdate(
                                update._id
                              );
                              // Refresh project data
                              const updatedProject =
                                await ProjectService.getProjectById(projectId);
                              setProject(updatedProject);
                            } catch (err) {
                              console.error("Error deleting update:", err);
                              setError("Failed to delete update.");
                            }
                          }
                        }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        <MdDelete className="text-lg" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No updates available for this project.
              </p>
              {(isAdmin || isEmp) && (
                <button
                  onClick={() =>
                    navigate(`/dashboard/projects/${projectId}/add-update`)
                  }
                  className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
                >
                  Add the first update
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === "billing" && client && !isEmp &&(
        <div className="space-y-6">
          {isAdmin && (
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => setShowBillingForm(!showBillingForm)}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
              >
                {showBillingForm ? "Cancel" : "Create Bill"}
              </button>
            </div>
          )}

          {/* Add Billing Form */}
          {showBillingForm && isAdmin && (
            <div className="bg-gray-50 p-6 rounded-md mb-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Create New Bill
              </h2>
              <form onSubmit={handleCreateBillingItem}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newBillingItem.title}
                      onChange={handleBillingInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={newBillingItem.description}
                      onChange={handleBillingInputChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount
                    </label>
                    <input
                      type="number"
                      name="amount"
                      value={newBillingItem.amount}
                      onChange={handleBillingInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      name="currency"
                      value={newBillingItem.currency}
                      onChange={handleBillingInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      name="dueDate"
                      value={newBillingItem.dueDate}
                      onChange={handleBillingInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={newBillingItem.status}
                      onChange={handleBillingInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="PENDING">Pending</option>
                      <option value="PAID">Paid</option>
                      <option value="OVERDUE">Overdue</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowBillingForm(false)}
                    className="mr-2 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                  >
                    Create Bill
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Billing Items List */}
          <div className="bg-white rounded-md">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Project Billing
            </h2>

            {billingItems && billingItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Title
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Amount
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Due Date
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      {isAdmin && (
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {billingItems.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link to={`/dashboard/billing-items/${item._id}`}>
                            <div className="text-sm font-medium text-gray-900">
                              {item.title}
                            </div>
                            <div
                              className="text-sm text-gray-500 "
                              title={item.description}
                            >
                              {item.description.length > 45
                                ? item.description.slice(0, 45) + '...'
                                : item.description
                              }
                            </div>
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.currency}{" "}
                            {parseFloat(item.amount).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(item.dueDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={item.status} />
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() =>
                                navigate(`/dashboard/billing-items/${item._id}`)
                              }
                              className="text-blue-600 hover:text-blue-800 mr-3"
                            >
                              <FaEye className="text-lg" />
                            </button>
                            {
                              <button
                                onClick={() =>
                                  navigate(
                                    `/dashboard/billing-items/${item._id}/edit`
                                  )
                                }
                                className="text-indigo-600 hover:text-indigo-800 mr-3"
                              >
                                <FaEdit className="text-lg" />
                              </button>
                            }
                            <button
                              onClick={() => handleDeleteBillingItem(item._id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <MdDelete className="text-lg" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  No billing items available for this project.
                </p>
                {isAdmin && !showBillingForm && (
                  <button
                    onClick={() => setShowBillingForm(true)}
                    className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Create your first bill
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Payment Summary */}
          {isEmp && billingItems && billingItems.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Payment Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm font-medium text-gray-500">
                    Total Billed
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {billingItems
                      .reduce(
                        (total, item) => total + parseFloat(item.amount),
                        0
                      )
                      .toLocaleString()}{" "}
                    {billingItems[0]?.currency || "INR"}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm font-medium text-gray-500">
                    Total Paid
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {billingItems
                      .reduce((total, item) => {
                        const paidAmount = item.paidAmount
                          ? item.paidAmount.reduce(
                            (sum, payment) =>
                              sum + parseFloat(payment.amount),
                            0
                          )
                          : 0;
                        return total + paidAmount;
                      }, 0)
                      .toLocaleString()}{" "}
                    {billingItems[0]?.currency || "INR"}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm font-medium text-gray-500">
                    Outstanding Balance
                  </p>
                  <p className="text-2xl font-bold text-red-600">
                    {billingItems
                      .reduce((total, item) => {
                        const paidAmount = item.paidAmount
                          ? item.paidAmount.reduce(
                            (sum, payment) =>
                              sum + parseFloat(payment.amount),
                            0
                          )
                          : 0;
                        return total + (parseFloat(item.amount) - paidAmount);
                      }, 0)
                      .toLocaleString()}{" "}
                    {billingItems[0]?.currency || "INR"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewProject;